// Phase 9 — Dashboard-first UI rebalance. /payments route with tab bar.
//
// Tabs: List (default), Review, Recurring, Insights. Each tab renders its
// sub-view inline. The `tab` URL search param drives which tab is active.
// Legacy routes (/payments/review, /payments/recurring, /insights) redirect
// here with the correct tab parameter.
//
// The List tab is the existing virtualised payments list with CRUD. Review,
// Recurring, and Insights are lazy-loaded from their existing route modules.

import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { PaymentTransaction } from '@dailydeck/shared/types';
import { copy } from '@dailydeck/shared/copy';
import { usePayments } from '../../hooks/usePayments.js';
import { PaymentRow } from './PaymentRow.js';
import { DateRangeFilter } from './DateRangeFilter.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { PaymentsToolbar } from '../../components/PaymentsToolbar.js';
import type { PaymentDialogMode } from './PaymentDialog.js';

const PaymentDialog = React.lazy(() =>
  import('./PaymentDialog.js').then((m) => ({ default: m.PaymentDialog })),
);
const DeletePaymentDialog = React.lazy(() =>
  import('./DeletePaymentDialog.js').then((m) => ({ default: m.DeletePaymentDialog })),
);
const StatementUploadDialog = React.lazy(() =>
  import('./StatementUploadDialog.js').then((m) => ({ default: m.StatementUploadDialog })),
);
const ReviewTab = React.lazy(() =>
  import('./review/Review.js').then((m) => ({ default: m.Review })),
);
const RecurringTab = React.lazy(() =>
  import('./recurring/Recurring.js').then((m) => ({ default: m.Recurring })),
);
const InsightsTab = React.lazy(() =>
  import('../../routes/insights/Insights.js').then((m) => ({ default: m.Insights })),
);
import {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
} from '../../components/ui/Toast.js';

type PaymentsTab = 'list' | 'review' | 'recurring' | 'insights';

const TABS: { id: PaymentsTab; label: string }[] = [
  { id: 'list', label: 'List' },
  { id: 'review', label: 'Review' },
  { id: 'recurring', label: 'Recurring' },
  { id: 'insights', label: 'Insights' },
];

const VIRTUALISE_THRESHOLD = 50;
const ROW_HEIGHT = 56;
/** Test hook — re-exported so unit tests can assert the threshold without
    duplicating the constant. */
export const _PAYMENTS_VIRTUALISE_THRESHOLD = VIRTUALISE_THRESHOLD;

interface VirtualisedListProps {
  payments: readonly PaymentTransaction[];
  today: Date;
  onRowClick: (payment: PaymentTransaction) => void;
}

function VirtualisedList({ payments, today, onRowClick }: VirtualisedListProps): JSX.Element {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: payments.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });

  return (
    <div
      ref={parentRef}
      data-testid="payments-virtual-scroll"
      className="rounded-lg border border-border-subtle bg-surface-raised overflow-auto"
      style={{ maxHeight: '70vh' }}
    >
      <div
        style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}
      >
        {virtualizer.getVirtualItems().map((vRow) => {
          const payment = payments[vRow.index];
          if (payment === undefined) return null;
          return (
            <div
              key={payment.id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${vRow.size}px`,
                transform: `translateY(${vRow.start}px)`,
              }}
              role="button"
              tabIndex={0}
              onClick={() => onRowClick(payment)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(payment);
                }
              }}
              className="cursor-pointer"
            >
              <PaymentRow payment={payment} today={today} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PlainListProps {
  payments: readonly PaymentTransaction[];
  today: Date;
  onRowClick: (payment: PaymentTransaction) => void;
}

function PlainList({ payments, today, onRowClick }: PlainListProps): JSX.Element {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-raised overflow-hidden" data-testid="payments-plain-list">
      {payments.map((payment) => (
        <div
          key={payment.id}
          role="button"
          tabIndex={0}
          onClick={() => onRowClick(payment)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRowClick(payment);
            }
          }}
          className="cursor-pointer"
          data-testid="payment-row-trigger"
        >
          <PaymentRow payment={payment} today={today} />
        </div>
      ))}
    </div>
  );
}

interface ToastState {
  id: number;
  title: string;
  description?: string;
  variant: 'default' | 'danger';
  /** Optional CTA — when present, renders a Link inside the toast body. */
  cta?: { label: string; to: string };
}

export function Payments(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.payments.pageTitle}`;
  }, []);

  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as PaymentsTab | null) ?? 'list';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const setTab = React.useCallback(
    (next: PaymentsTab) => {
      const updated = new URLSearchParams(params);
      if (next === 'list') updated.delete('tab');
      else updated.set('tab', next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  const filterArgs: { from?: string; to?: string } = {};
  if (from !== '') filterArgs.from = from;
  if (to !== '') filterArgs.to = to;
  const { query, filtered } = usePayments(filterArgs);
  const today = React.useMemo(() => new Date(), []);

  // Dialog state
  const [paymentDialog, setPaymentDialog] = React.useState<{
    open: boolean;
    mode: PaymentDialogMode;
  }>({ open: false, mode: { kind: 'add' } });
  const [deleteTarget, setDeleteTarget] = React.useState<PaymentTransaction | null>(null);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [toast, setToast] = React.useState<ToastState | null>(null);

  const handleFilterChange = React.useCallback(
    (next: { from: string; to: string }) => {
      const updated = new URLSearchParams(params);
      if (next.from === '') updated.delete('from');
      else updated.set('from', next.from);
      if (next.to === '') updated.delete('to');
      else updated.set('to', next.to);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  const handleAdd = React.useCallback(() => {
    setPaymentDialog({ open: true, mode: { kind: 'add' } });
  }, []);

  const handleUpload = React.useCallback(() => {
    setUploadOpen(true);
  }, []);

  const handleRowClick = React.useCallback((payment: PaymentTransaction) => {
    setPaymentDialog({ open: true, mode: { kind: 'edit', payment } });
  }, []);

  const handlePaymentDialogChange = React.useCallback((next: boolean) => {
    setPaymentDialog((prev) => ({ ...prev, open: next }));
  }, []);

  const handleRequestDelete = React.useCallback((payment: PaymentTransaction) => {
    // Close the edit sheet first; deletion uses a Dialog (single modal stack).
    setPaymentDialog((prev) => ({ ...prev, open: false }));
    setDeleteTarget(payment);
  }, []);

  const handleDeleteOpenChange = React.useCallback((next: boolean) => {
    if (!next) setDeleteTarget(null);
  }, []);

  const handleDeleted = React.useCallback(() => {
    setToast({
      id: Date.now(),
      title: copy.payments.deletedToast,
      variant: 'danger',
    });
  }, []);

  const handleUploaded = React.useCallback(
    (result: { response: { unreviewedCount?: number } }) => {
      const unreviewed = result.response.unreviewedCount ?? 0;
      if (unreviewed > 0) {
        setToast({
          id: Date.now(),
          title: copy.payments.upload.successTitle,
          description: copy.payments.upload.successWithReviewBody(unreviewed),
          variant: 'default',
          cta: {
            label: copy.payments.toolbar.reviewLabel,
            to: '/payments/review',
          },
        });
      }
    },
    [],
  );

  return (
    <div className="space-y-6" data-testid="payments-route">
      <header className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-title-1 font-semibold text-text-primary">{copy.payments.pageTitle}</h1>
            <p className="text-body text-text-secondary mt-1">{copy.payments.pageSubtitle}</p>
          </div>
          {tab === 'list' && (
            <DateRangeFilter from={from} to={to} onChange={handleFilterChange} />
          )}
        </div>

        {/* Tab bar */}
        <nav className="flex gap-1 border-b border-border-subtle" role="tablist" aria-label="Payments sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-body-strong transition-colors duration-fast border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-accent-default text-accent-default'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === 'list' && <PaymentsToolbar onAdd={handleAdd} onUpload={handleUpload} />}
      </header>

      {/* ---- Tab: List ---- */}
      {tab === 'list' && (
        <>
          {/* Column header */}
          {query.isSuccess && filtered.length > 0 && (
            <div
              className="grid grid-cols-[7rem_1fr_8rem] gap-3 px-4 py-2 text-caption text-text-muted"
              aria-hidden="true"
            >
              <span>{copy.payments.columns.date}</span>
              <span>{copy.payments.columns.merchant}</span>
              <span className="text-right">{copy.payments.columns.amount}</span>
            </div>
          )}

          {query.isPending && (
            <div className="rounded-lg border border-border-subtle bg-surface-raised" aria-label={copy.payments.loadingLabel}>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          )}

          {query.isError && (
            <Card data-testid="payments-error">
              <CardHeader>
                <CardTitle>{copy.payments.errorTitle}</CardTitle>
                <CardDescription>{copy.errors.network_unavailable}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-body text-text-secondary">{copy.payments.errorBody}</p>
              </CardContent>
              <CardFooter>
                <Button variant="secondary" onClick={() => void query.refetch()}>
                  {copy.payments.retry}
                </Button>
              </CardFooter>
            </Card>
          )}

          {query.isSuccess && filtered.length === 0 && (
            <EmptyState
              title={copy.payments.emptyTitle}
              description={copy.payments.emptyBody}
            />
          )}

          {query.isSuccess && filtered.length > 0 && (
            filtered.length >= VIRTUALISE_THRESHOLD ? (
              <VirtualisedList payments={filtered} today={today} onRowClick={handleRowClick} />
            ) : (
              <PlainList payments={filtered} today={today} onRowClick={handleRowClick} />
            )
          )}
        </>
      )}

      {/* ---- Tab: Review ---- */}
      {tab === 'review' && (
        <React.Suspense fallback={<div className="rounded-lg border border-border-subtle bg-surface-raised p-6"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>}>
          <ReviewTab />
        </React.Suspense>
      )}

      {/* ---- Tab: Recurring ---- */}
      {tab === 'recurring' && (
        <React.Suspense fallback={<div className="rounded-lg border border-border-subtle bg-surface-raised p-6"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>}>
          <RecurringTab />
        </React.Suspense>
      )}

      {/* ---- Tab: Insights ---- */}
      {tab === 'insights' && (
        <React.Suspense fallback={<div className="rounded-lg border border-border-subtle bg-surface-raised p-6"><SkeletonRow /><SkeletonRow /></div>}>
          <InsightsTab />
        </React.Suspense>
      )}

      <React.Suspense fallback={null}>
        {paymentDialog.open && (
          <PaymentDialog
            open={paymentDialog.open}
            onOpenChange={handlePaymentDialogChange}
            mode={paymentDialog.mode}
            onRequestDelete={handleRequestDelete}
          />
        )}
        {deleteTarget !== null && (
          <DeletePaymentDialog
            open={deleteTarget !== null}
            onOpenChange={handleDeleteOpenChange}
            payment={deleteTarget}
            onDeleted={handleDeleted}
          />
        )}
        {uploadOpen && (
          <StatementUploadDialog
            open={uploadOpen}
            onOpenChange={setUploadOpen}
            onUploaded={handleUploaded}
          />
        )}
      </React.Suspense>

      {toast !== null && (
        <ToastProvider duration={5000}>
          <Toast
            key={toast.id}
            open
            onOpenChange={(o) => {
              if (!o) setToast(null);
            }}
            variant={toast.variant}
            data-testid="payments-toast"
          >
            <div className="flex flex-col gap-1">
              <ToastTitle>{toast.title}</ToastTitle>
              {toast.description !== undefined && (
                <ToastDescription>{toast.description}</ToastDescription>
              )}
              {toast.cta !== undefined && (
                <Link
                  to={toast.cta.to}
                  className="text-caption text-accent-default underline-offset-4 hover:underline"
                  onClick={() => setToast(null)}
                >
                  {toast.cta.label}
                </Link>
              )}
            </div>
            <ToastClose />
          </Toast>
          <ToastViewport />
        </ToastProvider>
      )}
    </div>
  );
}
